// IPv4 CIDR arithmetic for the site routes (CONTRACT 3.4 "Site routes"), shared by the Sites
// screen and the fixture API.

export interface Cidr {
  /** The address as an unsigned 32-bit number. */
  base: number;
  prefix: number;
}

const OCTET = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;

/** "192.168.40.21" -> 3232245781; null if it is not a dotted quad. */
export function ipToNumber(ip: string): number | null {
  const parts = ip.split('.');
  if (parts.length !== 4 || parts.some((p) => !OCTET.test(p))) return null;
  return parts.reduce((n, o) => n * 256 + Number(o), 0);
}

export function numberToIp(n: number): string {
  return [24, 16, 8, 0].map((s) => Math.floor(n / 2 ** s) % 256).join('.');
}

const blockSize = (prefix: number) => 2 ** (32 - prefix);

/** "192.168.40.0/24" -> { base, prefix }; host bits are kept, see `isNetworkAddress`. */
export function parseCidr(cidr: string): Cidr | null {
  const m = /^([\d.]+)\/(\d{1,2})$/.exec(cidr.trim());
  if (!m) return null;
  const base = ipToNumber(m[1]!);
  const prefix = Number(m[2]);
  if (base === null || prefix > 32) return null;
  return { base, prefix };
}

/** The network the CIDR names: "192.168.40.1/24" -> "192.168.40.0/24". */
export function canonicalCidr(c: Cidr): string {
  const size = blockSize(c.prefix);
  return `${numberToIp(Math.floor(c.base / size) * size)}/${c.prefix}`;
}

export function isNetworkAddress(c: Cidr): boolean {
  return c.base % blockSize(c.prefix) === 0;
}

/** First and last address the CIDR covers. */
function span(c: Cidr): [number, number] {
  const size = blockSize(c.prefix);
  const first = Math.floor(c.base / size) * size;
  return [first, first + size - 1];
}

export function cidrsOverlap(a: Cidr, b: Cidr): boolean {
  const [a0, a1] = span(a);
  const [b0, b1] = span(b);
  return a0 <= b1 && b0 <= a1;
}

export function cidrContains(c: Cidr, ip: string): boolean {
  const n = ipToNumber(ip);
  if (n === null) return false;
  const [first, last] = span(c);
  return n >= first && n <= last;
}

/** By network address, then the wider prefix first: the order the API stores routes in. */
export function compareCidrs(a: string, b: string): number {
  const x = parseCidr(a);
  const y = parseCidr(b);
  if (!x || !y) return a.localeCompare(b);
  return x.base - y.base || x.prefix - y.prefix;
}
